import { usePortal } from './PortalContext';
import { FileText, CheckCircle, Clock, AlertCircle, CreditCard, Download } from 'lucide-react';
import { format } from 'date-fns';

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);

const statusConfig = {
    draft: { label: 'Draft', icon: FileText, color: 'text-fg-3', bg: 'bg-fg-3/10' },
    pending: { label: 'Pending', icon: Clock, color: 'text-gold', bg: 'bg-gold-fill/10' },
    posted: { label: 'Awaiting Payment', icon: AlertCircle, color: 'text-warning', bg: 'bg-warning/10' },
    paid: { label: 'Paid', icon: CheckCircle, color: 'text-success', bg: 'bg-success/10' }
};

interface InvoiceListProps {
    onPayClick?: (invoiceId: string) => void;
}

export const InvoiceList = ({ onPayClick }: InvoiceListProps) => {
    const { invoices, targetedInvoiceId } = usePortal();

    if (invoices.length === 0) {
        return (
            <div className="text-center py-16 bg-card rounded-2xl border border-line">
                <FileText className="w-12 h-12 text-fg-4 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-fg mb-2">No Invoices Yet</h3>
                <p className="text-fg-3 text-sm">Your invoices will appear here once created.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-fg">Your Invoices</h2>
                <p className="text-sm text-fg-3">{invoices.length} total</p>
            </div>

            <div className="space-y-3">
                {invoices.map((invoice) => {
                    const status = statusConfig[invoice.status as keyof typeof statusConfig] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    const isPaid = invoice.status === 'paid';
                    const canPay = invoice.status === 'posted' || invoice.status === 'pending';
                    const isTargeted = targetedInvoiceId === invoice.id;

                    return (
                        <div
                            key={invoice.id}
                            className={`bg-card rounded-xl border p-4 transition-all ${isTargeted
                                ? 'border-[var(--portal-accent)] shadow-[0_0_15px_-5px_var(--portal-accent)] ring-1 ring-[var(--portal-accent)]'
                                : 'border-line hover:border-line-strong'
                                }`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                {/* Left: Invoice Details */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-mono font-bold text-fg">
                                            #{invoice.invoice_number || invoice.id.slice(0, 8)}
                                        </span>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${status.bg} ${status.color} flex items-center gap-1`}>
                                            <StatusIcon className="w-3 h-3" />
                                            {status.label}
                                        </span>
                                    </div>
                                    <p className="text-sm text-fg-3 truncate">
                                        {invoice.description}
                                    </p>
                                    <div className="flex items-center gap-3 mt-2 text-xs text-fg-4">
                                        <span>{format(new Date(invoice.date), 'dd MMM yyyy')}</span>
                                        {invoice.vehicle_reg && (
                                            <span className="font-mono">{invoice.vehicle_reg}</span>
                                        )}
                                        {invoice.job_number && (
                                            <span>Job #{invoice.job_number}</span>
                                        )}
                                    </div>
                                </div>

                                {/* Right: Amount & Actions */}
                                <div className="text-right flex flex-col items-end gap-2">
                                    <p className="font-mono font-bold text-lg text-fg">
                                        {formatCurrency(invoice.total_gross)}
                                    </p>
                                    {invoice.vat_amount > 0 && (
                                        <p className="text-xs text-fg-4">
                                            inc. {formatCurrency(invoice.vat_amount)} VAT
                                        </p>
                                    )}

                                    <div className="flex items-center gap-2 mt-1">
                                        {/* Download Button */}
                                        <button
                                            className="p-2 rounded-lg border border-line-strong hover:bg-raised transition-colors"
                                            title="Download PDF"
                                        >
                                            <Download className="w-4 h-4 text-fg-3" />
                                        </button>

                                        {/* Pay Button */}
                                        {canPay && onPayClick && (
                                            <button
                                                onClick={() => onPayClick(invoice.id)}
                                                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                                                style={{
                                                    backgroundColor: 'var(--portal-accent)',
                                                    color: 'white'
                                                }}
                                            >
                                                <CreditCard className="w-4 h-4" />
                                                Pay Now
                                            </button>
                                        )}

                                        {isPaid && (
                                            <span className="text-success text-sm flex items-center gap-1">
                                                <CheckCircle className="w-4 h-4" />
                                                Paid
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
